import React from "react";
import { useParams } from "react-router-dom";
import { TranslationKeys } from "translations";
import { DashboardTitle, Skeletons } from "components/common";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import useMonthlyReportService, { MonthlyReportClientDto } from "serviceHooks/useMonthlyReportService";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; report: MonthlyReportClientDto }
  | { kind: "error"; code: "not_found" | "load_failed" };

// Customer-facing detail page for `/{companyUrl}/MonthlyReports/:id`. It lives in `RouteConfig`, so it
// renders inside the company layout — the customer is logged in and a company is selected by the time
// we mount, which is what lets the company-scoped report endpoint answer at all. The body is
// server-rendered HTML injected via `dangerouslySetInnerHTML` (the server escapes raw HTML and strips
// dangerous-scheme links). Styling lives in `theme/styles/_monthlyReport.scss` (referenced via
// `cssExports`), not inline.
const MonthlyReportView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { getReport } = useMonthlyReportService();
  const [state, setState] = React.useState<LoadState>({ kind: "loading" });

  React.useEffect(() => {
    if (!id) {
      setState({ kind: "error", code: "not_found" });
      return;
    }
    // `cancelled` ignores a stale request whose `id` changed (or unmounted) before it resolved, so a
    // slow earlier fetch can't overwrite state for the report we're now showing.
    let cancelled = false;
    setState({ kind: "loading" });
    getReport(id)
      .then((report) => {
        if (!cancelled) setState({ kind: "loaded", report });
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 reads as "not found" too, so we never confirm a report exists for someone else.
        const status = err && err.status;
        const code = status === 404 || status === 403 ? "not_found" : "load_failed";
        setState({ kind: "error", code });
      });
    return () => {
      cancelled = true;
    };
    // Deps are `[id]` only: `getReport` is fresh each render, so including it would loop.
  }, [id]);

  // The heading sits outside renderBody so it stays put across loading, error and the report —
  // it is page chrome, not content. DashboardTitle is the report header the other report pages
  // use; no breadcrumb or toolbar here, since the customer reaches this page from a link and has
  // nothing to act on.
  return (
    <>
      <div className={cssExports.monthlyReportHeader}>
        <DashboardTitle title={TranslationKeys.MonthlyReport.Title} />
      </div>
      {renderBody(id, state)}
    </>
  );
};

const renderBody = (id: string, state: LoadState): React.ReactNode => {
  // The card is the known shape this page always resolves to, so the wait skeletons the content
  // region and keeps the card itself — the same treatment the advisor's review panel gives a report
  // being generated. The footer is left out: it attributes a report that isn't there yet.
  if (state.kind === "loading") {
    return (
      <div className={cssExports.monthlyReportCard} data-testid="monthly-report-loading">
        <div className={cssExports.monthlyReportContent}>
          <Skeletons.TextSkeleton lines={12} />
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    const messageKey =
      state.code === "not_found" ? TranslationKeys.MonthlyReport.NotFound : TranslationKeys.MonthlyReport.LoadFailed;
    return (
      <div className={cssExports.monthlyReportCard} data-testid="monthly-report-error">
        <div className={cssExports.monthlyReportContent}>
          <div className={cssExports.monthlyReportNotice}>
            {LocalizationHelperInstance.getFormattedMessage(messageKey, undefined, { id })}
          </div>
        </div>
      </div>
    );
  }
  return <ReportCard report={state.report} />;
};

// No Wint header here: the page renders inside the company layout, which already carries the branding.
const ReportCard: React.FC<{ report: MonthlyReportClientDto }> = ({ report }) => (
  <div className={cssExports.monthlyReportCard} data-testid="monthly-report-body">
    <div className={cssExports.monthlyReportContent}>
      <div className="wint-monthly-report" dangerouslySetInnerHTML={{ __html: report.currentRenderedHtml }} />
      <ReportFooter />
    </div>
  </div>
);

const ReportFooter: React.FC = () => (
  <div className={cssExports.monthlyReportFooter}>
    <div>
      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MonthlyReport.FooterAttribution)} ·{" "}
      <span className={cssExports.monthlyReportFooterTagline}>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MonthlyReport.FooterTagline)}
      </span>
    </div>
    <div>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MonthlyReport.FooterCompany)}</div>
  </div>
);

export default MonthlyReportView;
